import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, MousePointer, TrendingUp, TrendingDown, Minus, ChevronRight, BarChart3 } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import './NotificationStatsWidget.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping notification types to French labels
const NOTIFICATION_LABELS = {
  'AG_CREATED': 'Création AG',
  'COPRO_ADDED': 'Ajout coproprietaire',
  'BUILDING_CREATED': 'Création immeuble',
  'AG_CLOSED': 'Clôture AG',
  'IMPORT_COPRO_SUCCESS': 'Import coproprietaires',
  'IMPORT_BUILDING_SUCCESS': 'Import immeubles'
};

// Custom tooltip for sparkline
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = new Date(data.date);
    const formattedDate = date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
    return (
      <div className="sparkline-tooltip">
        <p className="text-[10px] text-slate-500">{formattedDate}</p>
        <p className="text-xs font-semibold text-slate-700">{data.count} notification{data.count > 1 ? 's' : ''}</p>
      </div>
    );
  }
  return null;
};

const NotificationStatsWidget = ({ token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [topNotifications, setTopNotifications] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      
      try {
        const [summaryRes, statsRes, dailyRes] = await Promise.all([
          axios.get(`${API}/stats/notifications/summary`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/stats/notifications?period=30d`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/stats/notifications/daily?days=7`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setSummary(summaryRes.data);
        // Get top 3 most viewed notifications
        const notifications = statsRes.data.notifications || [];
        setTopNotifications(notifications.slice(0, 3));
        // Set daily data for sparkline
        setDailyData(dailyRes.data.daily_stats || []);
      } catch (err) {
        console.error('Error fetching notification stats:', err);
        setError('Impossible de charger les statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  const getTrendIcon = () => {
    if (!summary) return null;
    if (summary.trend_direction === 'up') return <TrendingUp className="w-3.5 h-3.5 text-green-500" />;
    if (summary.trend_direction === 'down') return <TrendingDown className="w-3.5 h-3.5 text-red-500" />;
    return <Minus className="w-3.5 h-3.5 text-slate-400" />;
  };

  const getLabel = (type) => NOTIFICATION_LABELS[type] || type;

  if (loading) {
    return (
      <div className="notification-stats-widget" data-testid="notification-stats-widget-loading">
        <div className="p-4 md:p-5 border-b border-slate-100">
          <div className="h-5 bg-slate-200 rounded w-1/2 animate-pulse"></div>
        </div>
        <div className="p-3 md:p-4 space-y-3">
          <div className="h-16 bg-slate-100 rounded-lg animate-pulse"></div>
          <div className="h-12 bg-slate-100 rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification-stats-widget" data-testid="notification-stats-widget-error">
        <div className="p-4 text-center">
          <BarChart3 className="mx-auto text-slate-300 mb-2" size={24} />
          <p className="text-sm text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  // Don't show widget if no data
  if (!summary || summary.total_views_30d === 0) {
    return null;
  }

  return (
    <div className="notification-stats-widget" data-testid="notification-stats-widget">
      <div className="p-4 md:p-5 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <h2 className="text-sm md:text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Engagement utilisateurs
          </h2>
          <button 
            onClick={() => navigate('/analytics/notifications')}
            className="text-xs md:text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium flex items-center space-x-1 transition-colors"
            data-testid="view-all-stats-btn"
          >
            <span>Détails</span>
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="p-3 md:p-4 space-y-3">
        {/* Sparkline Chart - 7 days evolution */}
        {dailyData.length > 0 && (
          <div className="sparkline-container" data-testid="sparkline-chart">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] md:text-xs text-slate-500">7 derniers jours</p>
              <p className="text-[10px] md:text-xs font-medium text-slate-600">
                {dailyData.reduce((sum, d) => sum + d.count, 0)} total
              </p>
            </div>
            <div className="h-12">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                  <defs>
                    <linearGradient id="sparklineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00D3A8" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00D3A8" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#00D3A8" 
                    strokeWidth={2}
                    fill="url(#sparklineGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="stats-card">
            <div className="flex items-center justify-between mb-1">
              <div className="p-1.5 bg-blue-50 rounded-md">
                <Eye className="w-3.5 h-3.5 text-blue-500" />
              </div>
              {getTrendIcon()}
            </div>
            <p className="text-lg md:text-xl font-bold text-slate-800">
              {summary?.total_views_30d || 0}
            </p>
            <p className="text-[10px] md:text-xs text-slate-500">Notifications vues</p>
            {summary?.trend_vs_previous !== 0 && (
              <p className={`text-[9px] md:text-[10px] mt-0.5 ${summary?.trend_direction === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                {summary?.trend_direction === 'up' ? '+' : ''}{summary?.trend_vs_previous}% vs mois précédent
              </p>
            )}
          </div>

          <div className="stats-card">
            <div className="flex items-center justify-between mb-1">
              <div className="p-1.5 bg-green-50 rounded-md">
                <MousePointer className="w-3.5 h-3.5 text-green-500" />
              </div>
            </div>
            <p className="text-lg md:text-xl font-bold text-slate-800">
              {summary?.click_rate || 0}%
            </p>
            <p className="text-[10px] md:text-xs text-slate-500">Taux de clic</p>
            <p className="text-[9px] md:text-[10px] text-slate-400 mt-0.5">
              {summary?.total_clicks_30d || 0} clics
            </p>
          </div>
        </div>

        {/* Top Notifications */}
        {topNotifications.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[10px] md:text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Top notifications
            </p>
            <div className="space-y-1.5">
              {topNotifications.map((notif, index) => (
                <div key={index} className="flex items-center justify-between py-1.5 px-2 bg-slate-50/50 rounded-lg">
                  <span className="text-xs text-slate-600 truncate flex-1">
                    {getLabel(notif.notification_type)}
                  </span>
                  <div className="flex items-center gap-2 ml-2">
                    <span className="text-xs font-medium text-slate-700">
                      {notif.total_views}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                      notif.click_rate >= 50 
                        ? 'bg-green-100 text-green-700' 
                        : notif.click_rate >= 25 
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600'
                    }`}>
                      {notif.click_rate}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationStatsWidget;
